import React, { useState, useEffect } from 'react';
import {
  Button, Col, Row, Input, Spin,
  Table, Form, notification,
  Typography,
  Select,
  DatePicker,
  Modal,
} from 'antd';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';

import FilterRemoveIcon from 'mdi-react/FilterRemoveIcon';
import FilterIcon from 'mdi-react/FilterIcon';

import './AccountDeletionRequests.scss';
import { WarningOutlined } from '@ant-design/icons';
import { CoopercitrusUser } from '../../sharedTypes/UserTypes';
import {
  del,
  get, patch, RequestWrapper, ResponseWrapper, isSuperAgentError,
} from '../../dataSource/requests';

const AccountDeletionRequests = (): JSX.Element => {
  const navigate = useNavigate();

  const [formInstance] = Form.useForm();

  const [loading, setLoading] = useState<boolean>(false);
  const [tableDataSource, setTableDataSource] = useState<Array<Branch>>([]);
  const [tableLoading, setTableLoading] = useState<boolean>(false);
  const [tablePagination, setTablePagination] = useState<TablePagination>({
    current: 1,
    defaultCurrent: 1,
    pageSize: 15,
    showQuickJumper: true,
    total: 0,
  });
  const [users, setUsers] = useState<CoopercitrusUser[]>([]);

  const fetchUsers = async (): Promise<void> => {
    let response: ResponseWrapper | undefined;

    const requestReference: RequestWrapper = get({
      url: '/role/findTypesWithAllUsers',

      frontConfigs: {
        navigateFunction: navigate,
      },
    });

    try {
      response = await requestReference;
    } catch (error) {
      notification.error({
        message: 'Erro',
        description: 'Erro ao carregar a listagem de usuários cooperados e coopercitrus',
      });
    }

    switch (true) {
      case (response?.body?.code === 3010): {
        // "Listed types with users successfully", 3010
        const newUsers = response?.body.types;

        const newUserCooperative = newUsers
          .filter((v:any) => v.type === 'COOPERATIVE')
          .map((v:any) => v.users)
          .flat(1);

        const newUserCoopercitrus = newUsers
          .filter((v:any) => v.type === 'COOPERCITRUS')
          .map((v:any) => v.users)
          .flat(1);

        setUsers([...newUserCoopercitrus, ...newUserCooperative]);
      } break;

      default:
        notification.error({
          message: 'Erro',
          description: 'Erro ao carregar a listagem de usuários cooperados e coopercitrus',
        });
        break;
    }
  };

  const executeFilter = async (args: {
    page?: number,
    pageSize?: number,
    orderBy?: string,
    orderByDir?: string,
  } = {
    page: 0,
    pageSize: 15,
    orderBy: 'id',
    orderByDir: 'DESC',
  }): Promise<void> => {
    setTableLoading(true);
    let response: ResponseWrapper | undefined;

    const values = formInstance.getFieldsValue();

    const argsQuery = {
      orderBy: ('orderBy' in args) ? args.orderBy : 'id',
      page: ('page' in args && args.page >= 1) ? args?.page : 0,
      pageSize: ('pageSize' in args) ? args.pageSize : 15,
      sort: ('orderByDir' in args) ? args.orderByDir : 'DESC',
    };

    const requestReference: RequestWrapper = patch({
      url: '/userExclusionRequests/listAll',

      params: {
        ...Object.fromEntries(
          Object
            .entries(values)
            .filter(
              ([, elementValue]) => {
                return elementValue != null;
              },
            ),
        ),
      },

      query: {
        ...Object.fromEntries(
          Object
            .entries(argsQuery)
            .filter(
              ([, elementValue]) => {
                return elementValue != null;
              },
            ),
        ),
      },

      frontConfigs: {
        navigateFunction: navigate,
      },
    });

    try {
      response = await requestReference;
    } catch (error: unknown) {
      console.log('error', error);
    }

    switch (true) {
      case (response != null && response?.body?.code === 10000): {
        // "Notification category request successfully", 10000
        const pagination = tablePagination;
        pagination.pageSize = response?.body?.paginationInfo?.maxSize;
        pagination.current = response?.body?.paginationInfo?.pageNumber + 1;
        pagination.total = response?.body?.paginationInfo?.totalElements;

        setTableDataSource(response?.body?.userExclusionRequestsOutputs);

        setTablePagination((previous) => {
          return ({
            ...previous,
            current: pagination.current,
            defaultCurrent: pagination.defaultCurrent,
            pageSize: pagination.pageSize,
            showQuickJumper: pagination.showQuickJumper,
            total: pagination.total,
          });
        });
      } break;

      default:
        notification.error({
          message: 'Erro',
          description: 'Desculpe, houve um erro ao pesquisar as solicitações de exclusão de conta, por favor tente novamente mais tarde.',
        });
        break;
    }

    setTableLoading(false);
  };

  const handleTableChange = (pagination: any, _filters: any, sorter: any): void => {
    setTableLoading(true);

    setTablePagination((previous) => {
      return ({
        ...previous,
        current: pagination.current || previous.current,
        defaultCurrent: pagination.defaultCurrent || previous.defaultCurrent,
        pageSize: pagination.pageSize || previous.pageSize,
        showQuickJumper: pagination.showQuickJumper
          || previous.showQuickJumper,
        total: pagination.total || previous.total,
      });
    });

    let finalOrderDir;

    if (sorter?.field && sorter?.order) {
      switch (true) {
        case (sorter.order === 'descend'):
          finalOrderDir = 'DESC';
          break;

        case (sorter.order === 'ascend'):
        case (sorter.order == null):
        default:
          finalOrderDir = 'ASC';
          break;
      }
    }

    executeFilter({
      page: (pagination?.current)
        ? pagination?.current - 1
        : 0,
      orderBy: sorter?.field || 'id',
      orderByDir: finalOrderDir || 'DESC',
      pageSize: pagination.pageSize,
    });
  };

  const deletionRequests = async (id:number):Promise<void> => {
    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;

    const requestReference: RequestWrapper = del({
      url: `/user/acceptUserDeletionRequest/${id}`,
      frontConfigs: {
        navigateFunction: navigate,
      },
    });

    try {
      response = await requestReference;
    } catch (error: unknown) {
      if (isSuperAgentError(error)) {
        responseError = error.response;
      }
    }

    switch (true) {
      case (response != null && response?.body?.code === 1022): {
        executeFilter();
        break;
      }

      case (responseError?.body?.code === -1034): {
        notification.error({
          message: 'Erro',
          description: 'A solicitação de exclusão não foi encontrada',
        });

        break;
      }

      case (responseError?.body?.code === -1035): {
        notification.error({
          message: 'Erro',
          description: 'Esta solicitação não está aguardando aprovação',
        });

        break;
      }

      default:
        notification.error({
          message: 'Erro',
          description: 'Desculpe, houve um erro ao pesquisar as solicitações de exclusão de conta, por favor tente novamente mais tarde.',
        });
        break;
    }
  };

  useEffect(() => {
    executeFilter();
    fetchUsers();

    return () => {
      setLoading(false);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="AccountDeletionRequests">
      <Spin
        tip="Carregando..."
        spinning={loading}
      >
        <Row
          className="AccountDeletionRequests__ComponentRow"
          gutter={[24, 24]}
        >
          <Col
            className="AccountDeletionRequests__ComponentColumn"
            xs={{ span: 24 }}
          >
            <Row
              gutter={[24, 24]}
              justify="space-between"
              className="AccountDeletionRequests__PageTitleRow"
            >
              <Col
                className="title-container"
              >
                <span
                  className="page-title"
                >
                  Solicitações de exclusão de conta
                </span>
              </Col>
            </Row>
          </Col>
        </Row>

        <Form
          scrollToFirstError
          layout="vertical"
          form={formInstance}
          onFinish={
          async (): Promise<void> => {
            await executeFilter();
          }
        }
        >
          <Row
            gutter={24}
            className="filters-container"
          >
            <Col xs={{ span: 24 }} lg={{ span: 6 }}>
              <Form.Item label="Usuário solicitante" name="requestedByName" rules={[]}>
                <Select
                  showSearch
                  allowClear
                  style={{ width: '100%' }}
                  placeholder="Usuário"
                  filterOption={(input, option): any => option?.['props']?.children
                    .toLowerCase()
                    .includes(input.toLowerCase())}
                >
                  {users.map((v:any) => (
                    <Select.Option key={v.id} value={v.name}>
                      {v.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={{ span: 24 }} lg={{ span: 6 }}>
              <Form.Item
                label="BP do usuário solicitante"
                name="requestedByBp"
                rules={[]}
              >
                <Input
                  style={{ width: '100%' }}
                  placeholder="Digite o BP do usuário solicitante"
                />
              </Form.Item>
            </Col>
            <Col xs={{ span: 24 }} lg={{ span: 6 }}>
              <Form.Item
                label="ID do usuário solicitante"
                name="requestedById"
                rules={[]}
              >
                <Input
                  style={{ width: '100%' }}
                  placeholder="Digite o ID do usuário solicitante"
                />
              </Form.Item>
            </Col>
            <Col xs={{ span: 24 }} lg={{ span: 6 }}>
              <Form.Item
                label="Data da solicitação da exclusão"
                name="requestTimestamp"
                rules={[]}
              >
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
              </Form.Item>
            </Col>
            <Col xs={{ span: 24 }} lg={{ span: 6 }}>
              <Form.Item label="Usuário aprovador" name="approvedByName" rules={[]}>
                <Select
                  showSearch
                  allowClear
                  style={{ width: '100%' }}
                  placeholder="Usuário"
                  filterOption={(input, option): any => option?.['props']?.children
                    .toLowerCase()
                    .includes(input.toLowerCase())}
                >
                  {users.map((v:any) => (
                    <Select.Option key={v.id} value={v.name}>
                      {v.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={{ span: 24 }} lg={{ span: 6 }}>
              <Form.Item
                label="BP do usuário aprovador"
                name="approvedByBp"
                rules={[]}
              >
                <Input
                  style={{ width: '100%' }}
                  placeholder="Digite o BP do usuário aprovador"
                />
              </Form.Item>
            </Col>
            <Col xs={{ span: 24 }} lg={{ span: 6 }}>
              <Form.Item
                label="ID do usuário aprovador"
                name="approvedById"
                rules={[]}
              >
                <Input
                  style={{ width: '100%' }}
                  placeholder="Digite o ID do usuário aprovador"
                />
              </Form.Item>
            </Col>
            <Col xs={{ span: 24 }} lg={{ span: 6 }}>
              <Form.Item
                label="Data da aprovação da exclusão"
                name="approvementTimestamp"
                rules={[]}
              >
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
              </Form.Item>
            </Col>

            <Col xs={{ span: 24 }} lg={{ span: 6 }}>
              <Form.Item
                label="ID da solicitação de exclusão de conta"
                name="requestId"
                rules={[]}
              >
                <Input
                  style={{ width: '100%' }}
                  placeholder="Digite o ID da solicitação"
                />
              </Form.Item>
            </Col>
            <Col xs={{ span: 24 }} lg={{ span: 6 }}>
              <Form.Item
                label="Status da solicitação de exclusão de conta"
                name="requestStatus"
                rules={[]}
              >
                <Select
                  showSearch
                  allowClear
                  style={{ width: '100%' }}
                  placeholder="Selecione o status"
                >
                  <Select.Option value="APPROVED">
                    Aprovada
                  </Select.Option>
                  <Select.Option value="WAITING_APPROVEMENT">
                    Aguardando aprovação
                  </Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={{ span: 12 }} lg={{ span: 6 }} />
            <Row className="buttons">
              <Col span={12}>
                <Form.Item
                  label=" "
                >
                  <Button
                    className="btn-clean"
                    htmlType="reset"
                    onClick={
                  (): void => {
                    formInstance.resetFields();
                    executeFilter();
                  }
                }
                  >
                    <FilterRemoveIcon />
                    Limpar
                  </Button>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label=" "
                >
                  <Button
                    className="btn-filter"
                    type="primary"
                    htmlType="submit"
                  >
                    <FilterIcon />
                    Filtrar
                  </Button>
                </Form.Item>
              </Col>
            </Row>
          </Row>
          <Row gutter={24} className="list-container">
            <Col span={24}>
              <Row gutter={24}>
                <Col span={24}>
                  <Table
                    className="AccountDeletionRequests__Table"
                    columns={[
                      {
                        title:
  <div className="table-titles">
    ID
    <br />
    Solicitação
  </div>,
                        dataIndex: 'id',
                        key: 'id',
                        ellipsis: true,
                        sorter: true,
                        width: 125,
                        defaultSortOrder: 'descend',
                      },
                      {
                        title: <div className="table-titles">Nome solicitante</div>,
                        key: 'requestedByName',
                        dataIndex: 'requestedByName',
                        ellipsis: true,
                        sorter: true,
                        width: 300,
                      },
                      {
                        title: <div className="table-titles">BP solicitante</div>,
                        key: 'requestedByBp',
                        dataIndex: 'requestedByBp',
                        ellipsis: true,
                        sorter: true,
                        width: 150,
                      },
                      {
                        title:
  <div className="table-titles">
    ID
    <br />
    solicitante
  </div>,
                        key: 'requestedById',
                        dataIndex: 'requestedById',
                        ellipsis: true,
                        sorter: true,
                        width: 125,
                      },
                      {
                        title:
  <div className="table-titles">
    Data/hora da
    <br />
    solicitação
  </div>,
                        dataIndex: 'creationDate',
                        key: 'creationDate',
                        ellipsis: true,
                        sorter: true,
                        render: (text: string):any => {
                          if (text) {
                            return <span>{moment(text).format('DD/MM/YYYY [às] HH:mm')}</span>;
                          }

                          return '';
                        },
                        width: 170,
                      },
                      {
                        title: <div className="table-titles">Nome aprovador</div>,
                        dataIndex: 'approvedByName',
                        key: 'approvedByName',
                        ellipsis: true,
                        sorter: true,
                        width: 300,
                      },
                      {
                        title: <div className="table-titles">BP aprovador</div>,
                        dataIndex: 'approvedByBp',
                        key: 'approvedByBp',
                        ellipsis: true,
                        sorter: true,
                        width: 150,
                      },
                      {
                        title:
  <div className="table-titles">
    ID
    <br />
    aprovador
  </div>,
                        dataIndex: 'approvedById',
                        key: 'approvedById',
                        ellipsis: true,
                        sorter: true,
                        width: 125,
                      },
                      {
                        title:
  <div className="table-titles">
    Data/hora da
    <br />
    aprovação
  </div>,
                        dataIndex: 'approvedAt',
                        key: 'approvedAt',
                        ellipsis: true,
                        sorter: true,
                        render: (text: string):any => {
                          if (text) {
                            return <span>{moment(text).format('DD/MM/YYYY [às] HH:mm')}</span>;
                          }

                          return '';
                        },
                        width: 170,
                      },
                      {
                        title:
  <div className="table-titles">
    Status
    <br />
    solicitação
  </div>,
                        dataIndex: 'status',
                        key: 'status',
                        ellipsis: true,
                        sorter: true,
                        render: (
                          _value: string | number,
                          record: any,
                        ): React.ReactNode => {
                          if (record.status === 'WAITING_APPROVEMENT') {
                            return 'Aguardando aprovação';
                          }
                          return 'Aprovada';
                        },
                        width: 225,
                      },
                      {
                        title:
  <div className="table-titles">
    Ação de
    <br />
    aprovação
  </div>,
                        dataIndex: 'id',
                        key: 'opcoes',
                        render: (
                          _value: string | number,
                          record: any,
                        ): React.ReactNode => {
                          if (record.status === 'WAITING_APPROVEMENT') {
                            return (

                              <Button
                                className="btn-filter"
                                type="primary"
                                href="#"
                                onClick={():void => {
                                  Modal.confirm({
                                    title: 'Atenção',
                                    content: 'Deseja realmente aprovar esta solicitação de exclusão de conta?',
                                    okText: 'Sim',
                                    cancelText: 'Não',
                                    autoFocusButton: null,
                                    icon: (<WarningOutlined />),
                                    onOk: () => {
                                      deletionRequests(record.id);
                                    },
                                  });
                                }}
                              >
                                Aprovar
                              </Button>
                            );
                          }
                          return 'Realizada';
                        },
                        width: 170,
                      },
                    ]}
                    dataSource={tableDataSource}
                    loading={tableLoading}
                    rowKey={(record): string => record.id as unknown as string}
                    pagination={{
                      defaultCurrent: tablePagination.defaultCurrent || 1,
                      current: tablePagination.current || 1,
                      pageSize: tablePagination.pageSize || 15,
                      total: tablePagination.total || 0,
                      showQuickJumper: tablePagination.showQuickJumper || true,
                      pageSizeOptions: ['1', '15', '50', '100'],
                      showSizeChanger: true,
                    }}
                    onChange={handleTableChange}
                    scroll={{
                      x: '100vw',
                    }}
                  />
                </Col>
              </Row>
            </Col>
          </Row>
        </Form>
      </Spin>
    </div>
  );
};

export default AccountDeletionRequests;
